import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import GuestHeader from "@/components/GuestHeader";
import { api, formatErr } from "@/lib/api";
import { getGuest } from "@/lib/guest";
import { useAppTz, zonedIsoFromWallTime, tzAbbr } from "@/lib/tz";
import { useAvailabilityEvents } from "@/lib/useAvailabilityEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CheckCircle2, Clock3, MapPin, Users } from "lucide-react";

function localDateKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function GuestModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const guest = getGuest() || {};
  const tz = useAppTz();
  const [module, setModule] = useState(null);
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [date, setDate] = useState(localDateKey());
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availabilityVersion, setAvailabilityVersion] = useState(0);
  const [duration, setDuration] = useState(60);
  const [participants, setParticipants] = useState(1);
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/public/modules"),
      api.get(`/public/modules/${moduleId}/content`),
    ]).then(([mods, workspace]) => {
      if (!alive) return;
      const m = (mods.data?.items || []).find((x) => x.id === moduleId);
      if (!m) { navigate("/experience", { replace: true }); return; }
      setModule(m);
      setSettings(workspace.data?.settings || {});
      setServices((workspace.data?.content || []).filter((x) => x.active !== false && x.guest_visible !== false));
      setResources((workspace.data?.resources || []).filter((x) => x.active !== false));
    }).catch(() => navigate("/experience", { replace: true }));
    return () => { alive = false; };
  }, [moduleId, navigate]);

  const selectedService = services.find((x) => x.id === serviceId);
  const selectedResource = resources.find((x) => x.id === resourceId);
  const bookingEngine = ["appointment", "resource"].includes(module?.engine);
  const requestEngine = module?.engine === "request";

  useEffect(() => {
    if (!services.length || serviceId) return;
    setServiceId(services[0].id);
  }, [services, serviceId]);

  useEffect(() => {
    if (!selectedService) return;
    setDuration(Number(selectedService.duration_min || settings.default_duration_min || 60));
    setParticipants((p) => Math.min(p || 1, Number(selectedService.capacity || 999)));
  }, [selectedService, settings.default_duration_min]);

  useEffect(() => {
    if (module?.engine === "resource" && resources.length && !resourceId) setResourceId(resources[0].id);
  }, [module?.engine, resources, resourceId]);

  const maxParticipants = useMemo(() => {
    const caps = [selectedService?.capacity, selectedResource?.capacity].filter(Boolean).map(Number);
    return caps.length ? Math.min(...caps) : 999;
  }, [selectedService, selectedResource]);

  const loadAvailability = useCallback(async () => {
    if (!module || !bookingEngine || !serviceId || !date) return;
    setSlotsLoading(true);
    try {
      const { data } = await api.get(`/public/modules/${module.id}/availability`, { params: {
        date, service_id: serviceId, resource_id: resourceId || undefined, guest_id: guest.id || undefined, participants: Number(participants || 1),
      }});
      const next = data?.slots || [];
      setSlots(next);
      if (time && !next.some((s) => s.time === time && s.available)) {
        setTime("");
        toast.info("Availability updated — the previously selected time is no longer available.");
      }
    } catch { setSlots([]); } finally { setSlotsLoading(false); }
  }, [module, bookingEngine, serviceId, resourceId, date, participants, guest.id, time]);

  useEffect(() => { loadAvailability(); }, [loadAvailability, availabilityVersion]);

  useAvailabilityEvents((ev) => {
    if (!ev?.type) return;
    if (ev.type === "availability.changed" && ev.module_id === moduleId) {
      setAvailabilityVersion((v) => v + 1);
    }
  });

  const identity = {
    guest_id: guest.id,
    guest_name: guest.name,
    cabin_id: guest.cabin_id,
    cabin_name: guest.cabin,
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!module) return;
    if (requestEngine) {
      if (!serviceId && services.length) { toast.error("Select a request type"); return; }
      setSending(true);
      try {
        const selected = selectedService || services[0];
        const { data } = await api.post("/public/platform/requests", {
          ...identity,
          module_id: module.id,
          title: selected?.name || settings.guest_title || module.name,
          details,
          priority: "normal",
        });
        setResult(data);
        setDone(true);
        toast.success("Request sent to the crew");
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || "Could not send the request");
      } finally { setSending(false); }
      return;
    }
    if (!bookingEngine) {
      toast.error("This service type is not available in this release yet.");
      return;
    }
    if (!serviceId) { toast.error("Select a service"); return; }
    if (module.engine === "resource" && !resourceId) { toast.error("Select a resource / location"); return; }
    if (!date || !time) { toast.error("Choose a date and time"); return; }

    const [hh, mm] = time.split(":").map(Number);
    const start = zonedIsoFromWallTime(date, hh, mm, tz);
    const end = new Date(new Date(start).getTime() + Number(duration || 60) * 60000).toISOString();
    setSending(true);
    try {
      const { data } = await api.post("/public/platform/reservations", {
        ...identity,
        module_id: module.id,
        service_id: selectedService.id,
        service_name: selectedService.name,
        resource_id: selectedResource?.id || null,
        resource_name: selectedResource?.name || null,
        start_at: start,
        end_at: end,
        participants: Number(participants || 1),
        notes: settings.allow_guest_notes === false ? "" : details,
      });
      setResult(data);
      setDone(true);
      toast.success(data?.status === "requested" ? "Booking request sent" : "Booking confirmed");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "object") {
        toast.error(detail.message || "That time is not available");
      } else {
        toast.error(formatErr(detail) || "Could not create the booking");
      }
    } finally { setSending(false); }
  };

  if (!module) return <div className="min-h-screen bg-background"/>;

  return <div className="min-h-screen bg-background">
    <GuestHeader guestName={guest.name}/>
    <main className="max-w-2xl mx-auto px-6 py-12 md:py-20">
      <button onClick={() => navigate("/experience")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent"><ArrowLeft className="h-4 w-4"/>All services</button>
      <div className="overline text-accent mt-10 mb-3">{module.category} · {requestEngine ? "request" : "booking"}</div>
      <h1 className="font-display text-5xl">{settings.guest_title || module.name}</h1>
      <p className="text-muted-foreground mt-4">{settings.guest_intro || module.description}</p>
      {settings.availability_note && <p className="mt-4 text-sm border-l-2 border-accent pl-4">{settings.availability_note}</p>}

      {done ? <div className="mt-10 border border-accent/40 bg-accent/5 p-8">
        <CheckCircle2 className="h-8 w-8 text-accent"/>
        <div className="font-display text-2xl mt-4">{requestEngine ? "Request sent" : result?.status === "requested" ? "Booking request received" : "Booking confirmed"}</div>
        <p className="text-muted-foreground mt-2">{requestEngine ? (selectedService?.name || module.name) : <>{selectedService?.name}{selectedResource ? ` · ${selectedResource.name}` : ""}</>}</p>
        <p className="text-sm text-muted-foreground mt-1">{requestEngine ? "The relevant crew have been notified." : "The crew calendar has been updated."}</p>
        <Button className="mt-6" onClick={() => navigate("/experience")}>Back to services</Button>
      </div> :
      <form onSubmit={submit} className="mt-10 border border-border bg-card p-6 md:p-8 space-y-5">
        {requestEngine ? <>
          {services.length > 0 && <div><Label>Request type</Label><Select value={serviceId} onValueChange={setServiceId}><SelectTrigger className="mt-2"><SelectValue placeholder="Select request"/></SelectTrigger><SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>}
          {selectedService?.description && <p className="text-sm text-muted-foreground">{selectedService.description}</p>}
          <label className="block"><span className="text-sm font-medium">Details for the crew</span><Textarea className="mt-2 min-h-28" placeholder="What can we help you with?" value={details} onChange={(e) => setDetails(e.target.value)}/></label>
          <Button type="submit" disabled={sending} className="w-full">{sending ? "Sending…" : "Send request"}</Button>
          <p className="text-xs text-muted-foreground text-center">Your request is sent to the authorised available crew assigned to this service.</p>
        </> : services.length === 0 ? <div className="p-6 border border-border text-muted-foreground">No guest-bookable services have been configured yet.</div> : <>
          <div><Label>Service</Label><Select value={serviceId} onValueChange={setServiceId}><SelectTrigger className="mt-2"><SelectValue placeholder="Select service"/></SelectTrigger><SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          {selectedService?.description && <p className="text-sm text-muted-foreground">{selectedService.description}</p>}

          {resources.length > 0 && <div><Label>{module.engine === "resource" ? "Location / resource" : "Preferred location (optional)"}</Label><Select value={resourceId || "none"} onValueChange={(v) => setResourceId(v === "none" ? "" : v)}><SelectTrigger className="mt-2"><SelectValue/></SelectTrigger><SelectContent>{module.engine !== "resource" && <SelectItem value="none">No preference</SelectItem>}{resources.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.location ? ` · ${r.location}` : ""}</SelectItem>)}</SelectContent></Select></div>}

          <div>
            <label className="block max-w-xs"><span className="text-sm font-medium">Date</span><Input type="date" className="mt-2" value={date} onChange={(e) => { setDate(e.target.value); setTime(""); }}/></label>
            <div className="flex items-center justify-between mt-5 mb-2">
              <span className="text-sm font-medium">Live availability · {tzAbbr(tz)}</span>
              <span className="text-xs text-muted-foreground">Preparation buffer {settings.prep_time_min || 0} min</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="live-booking-slots">
              {slotsLoading && !slots.length && <div className="col-span-full text-sm text-muted-foreground py-4">Checking live availability…</div>}
              {!slotsLoading && !slots.length && <div className="col-span-full text-sm text-muted-foreground py-4">No slots available for this day.</div>}
              {slots.map((slot) => <button key={slot.start_at} type="button" disabled={!slot.available} onClick={() => setTime(slot.time)} className={`h-11 rounded-md border text-sm transition-all ${time === slot.time ? "border-accent bg-accent text-accent-foreground" : slot.available ? "border-border hover:border-accent hover:bg-accent/5" : "border-border/40 bg-muted/40 text-muted-foreground/50 line-through cursor-not-allowed"}`}>{slot.time}</button>)}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Times update automatically if another guest, crew member or iPad books a conflicting slot.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-border p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Clock3 className="h-3 w-3"/>Duration</div><div className="mt-1">{duration} min</div></div>
            <label className="block"><span className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4"/>Participants</span><Input type="number" min="1" max={maxParticipants} className="mt-2" value={participants} onChange={(e) => setParticipants(e.target.value)}/></label>
          </div>

          {selectedResource && <div className="text-xs text-muted-foreground flex items-center gap-2"><MapPin className="h-3.5 w-3.5"/>{selectedResource.name}{selectedResource.location ? ` · ${selectedResource.location}` : ""} · capacity {selectedResource.capacity || 1}</div>}

          {settings.allow_guest_notes !== false && <label className="block"><span className="text-sm font-medium">Notes for the crew</span><Textarea className="mt-2 min-h-24" placeholder="Add any preference or detail…" value={details} onChange={(e) => setDetails(e.target.value)}/></label>}
          <Button type="submit" disabled={sending || !services.length || !time} className="w-full">{sending ? "Checking availability…" : settings.requires_confirmation ? "Request booking" : "Book now"}</Button>
        </>}
      </form>}
    </main>
  </div>;
}
